import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  RadioGroup,
  Radio,
  Checkbox,
  CheckboxGroup,
  Textarea,
  Button,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  useToast,
  Stack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Select,
} from '@chakra-ui/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaChevronLeft } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Componente Slider riutilizzabile con scala dinamica
const SliderComponent = ({
  value,
  onChange,
  min = 1,
  max = 10,
  labels = ['Min', 'Max'],
}) => (
  <VStack spacing={4} align="stretch">
    {/* Etichette estremi */}
    <HStack justify="space-between">
      <Text fontSize="sm" color="gray.600" fontWeight="bold">
        {labels[0]}
      </Text>
      <Text fontSize="sm" color="gray.600" fontWeight="bold">
        {labels[1]}
      </Text>
    </HStack>

    {/* Slider principale */}
    <Box position="relative">
      <Slider
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={1}
        colorScheme="teal"
        size="lg"
      >
        <SliderTrack height="6px" borderRadius="3px">
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb
          width="24px"
          height="24px"
          border="3px solid"
          borderColor="white"
          boxShadow="lg"
        >
          <Text fontSize="xs" fontWeight="bold" color="white">
            {value}
          </Text>
        </SliderThumb>
      </Slider>

      {/* Punti di riferimento sulla scala - dinamici */}
      <HStack justify="space-between" mt={2} px={2}>
        {Array.from({ length: max - min + 1 }, (_, i) => {
          const num = min + i;
          return (
            <Text key={num} fontSize="xs" color="gray.500" fontWeight="bold">
              {num}
            </Text>
          );
        })}
      </HStack>
    </Box>

    {/* Valore selezionato */}
    <Box
      textAlign="center"
      p={3}
      bg="teal.50"
      borderRadius="md"
      border="1px solid"
      borderColor="teal.200"
    >
      <Text fontSize="lg" fontWeight="bold" color="teal.600">
        Valore selezionato: {value}/{max}
      </Text>
    </Box>
  </VStack>
);

const FormDetail = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { userData, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({});
  const [formStructure, setFormStructure] = useState(null);
  const [formInfo, setFormInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [soci, setSoci] = useState([]);
  const [selectedSocioId, setSelectedSocioId] = useState(null);
  const [loadingSoci, setLoadingSoci] = useState(false);
  const [hasExistingResponse, setHasExistingResponse] = useState(false);
  const [checkingResponse, setCheckingResponse] = useState(false);
  const toast = useToast();

  // Alias per compatibilità
  const socio = userData;

  // Carica i soci dell'utente
  const loadSoci = async () => {
    if (!isAuthenticated || !userData?.id) return;

    try {
      setLoadingSoci(true);
      const response = await axios.get(`/api/soci?userId=${userData.id}`);

      if (response.data.data && response.data.data.length > 0) {
        setSoci(response.data.data);
        // Se c'è solo un socio, selezionalo automaticamente
        if (response.data.data.length === 1) {
          setSelectedSocioId(response.data.data[0].documentId);
        }
      } else {
        // Se non ci sono soci, non selezionare nessun socio
        setSelectedSocioId(null);
      }
    } catch (err) {
      console.error('Error loading soci:', err);
      // In caso di errore, non selezionare nessun socio
      setSelectedSocioId(null);
    } finally {
      setLoadingSoci(false);
    }
  };

  // Controlla se il socio ha già compilato questo form
  const checkExistingResponse = async (socioId) => {
    if (!socioId || !documentId || !isAuthenticated) return;

    try {
      setCheckingResponse(true);
      const response = await axios.get('/api/risposte-questionarios', {
        params: {
          documentId,
          socioId,
        },
      });

      // Se ci sono risposte per questo socio e questo form, disabilita il submit
      const hasResponse = response.data.data && response.data.data.length > 0;
      setHasExistingResponse(hasResponse);

      if (hasResponse) {
        toast({
          title: 'Form già compilato',
          description:
            'Hai già compilato questo form. Non puoi inviarlo nuovamente.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error checking existing response:', err);
      // In caso di errore, assumiamo che non ci sia una risposta esistente
      setHasExistingResponse(false);
    } finally {
      setCheckingResponse(false);
    }
  };

  // Carica i dettagli del form
  useEffect(() => {
    const loadFormDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get('/api/forms', {
          params: { documentId },
        });

        if (response.data.data && response.data.data.length > 0) {
          const form = response.data.data[0];

          // Memorizza le informazioni del form
          setFormInfo({
            image: form.image,
            descrizione: form.descrizione,
            titolo: form.titolo,
            stato: form.stato,
          });

          // Carica la struttura delle domande
          const domande = form.domande || form;
          setFormStructure(domande);

          // Inizializza formData con valori di default
          const initialData = {};
          if (domande.sections) {
            domande.sections.forEach((section) => {
              section.questions.forEach((question) => {
                if (question.type === 'slider') {
                  // Usa defaultValue se disponibile, altrimenti calcola un valore medio tra min e max
                  const min = question.min || 1;
                  const max = question.max || 10;
                  const midValue = Math.round((min + max) / 2);
                  initialData[question.id] = question.defaultValue || midValue;
                } else if (question.type === 'checkbox') {
                  initialData[question.id] = [];
                } else {
                  initialData[question.id] = '';
                }
              });
            });
          }
          setFormData(initialData);
        } else {
          setError('Form non trovato');
        }
      } catch (err) {
        console.error('Error loading form detail:', err);
        setError('Errore nel caricamento del form');
      } finally {
        setLoading(false);
      }
    };

    loadFormDetail();
  }, [documentId]);

  // Carica i soci quando l'utente è autenticato
  useEffect(() => {
    if (isAuthenticated && userData?.id) {
      loadSoci();
    }
  }, [isAuthenticated, userData?.id]);

  // Controlla se esiste già una risposta quando cambia il socio selezionato
  useEffect(() => {
    if (selectedSocioId && documentId && isAuthenticated) {
      checkExistingResponse(selectedSocioId);
    }
  }, [selectedSocioId, documentId, isAuthenticated]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field, values) => {
    setFormData((prev) => ({
      ...prev,
      [field]: values,
    }));
  };

  const ENFORCE_AUTH = false;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formStructure) {
      toast({
        title: 'Errore',
        description: 'Struttura del form non caricata',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Controlla se il form è già stato compilato
    if (hasExistingResponse) {
      toast({
        title: 'Form già compilato',
        description:
          'Hai già compilato questo form. Non puoi inviarlo nuovamente.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // TEMPORANEO: abilita ENFORCE_AUTH per richiedere autenticazione
    if (ENFORCE_AUTH && (!isAuthenticated || !socio || !socio.id)) {
      toast({
        title: 'Errore',
        description: 'Devi essere autenticato per compilare il form',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setSubmitting(true);

      // Invia le risposte al backend con documentId socio selezionato
      await axios.post('/api/risposte-questionarios', {
        risposte: formData,
        documentId,
        socioId: selectedSocioId, // ← Usa solo documentId socio selezionato
      });

      toast({
        title:
          formStructure.metadata?.successMessage?.title ||
          `Grazie ${socio.nome || socio.username || 'per il tuo feedback'}!`,
        description:
          formStructure.metadata?.successMessage?.description ||
          'I tuoi suggerimenti sono molto preziosi per noi.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Imposta che il form è stato compilato
      setHasExistingResponse(true);

      // Reset del form
      const initialData = {};
      if (formStructure.sections) {
        formStructure.sections.forEach((section) => {
          section.questions.forEach((question) => {
            if (question.type === 'slider') {
              // Usa defaultValue se disponibile, altrimenti calcola un valore medio tra min e max
              const min = question.min || 1;
              const max = question.max || 10;
              const midValue = Math.round((min + max) / 2);
              initialData[question.id] = question.defaultValue || midValue;
            } else if (question.type === 'checkbox') {
              initialData[question.id] = [];
            } else {
              initialData[question.id] = '';
            }
          });
        });
      }
      setFormData(initialData);
    } catch (err) {
      console.error('Error submitting form:', err);
      toast({
        title: 'Errore',
        description: "Errore nell'invio del form. Riprova più tardi.",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const value = formData[question.id] || '';

    switch (question.type) {
      case 'text':
      case 'email':
        return (
          <Input
            type={question.type}
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            required={question.required}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows={question.rows || 3}
          />
        );

      case 'radio':
        return (
          <RadioGroup
            value={value}
            onChange={(val) => handleInputChange(question.id, val)}
          >
            <Stack
              spacing={question.options.length > 2 ? 3 : 6}
              direction={question.options.length <= 2 ? 'row' : 'column'}
            >
              {question.options.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        );

      case 'checkbox':
        return (
          <CheckboxGroup
            value={value}
            onChange={(vals) => handleCheckboxChange(question.id, vals)}
          >
            <Stack spacing={3}>
              {question.options.map((option) => (
                <Checkbox key={option.value} value={option.value}>
                  {option.label}
                </Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
        );

      case 'slider':
        return (
          <SliderComponent
            value={value}
            onChange={(val) => handleInputChange(question.id, val)}
            min={question.min}
            max={question.max}
            labels={question.labels}
          />
        );

      default:
        return null;
    }
  };

  const renderConditionalField = (question) => {
    if (!question.conditionalField) return null;

    const showField =
      formData[question.id] === question.conditionalField.showWhen;
    if (!showField) return null;

    const conditionalQuestion = question.conditionalField;
    const value = formData[conditionalQuestion.id] || '';

    return (
      <FormControl key={conditionalQuestion.id} mt={4}>
        <FormLabel>{conditionalQuestion.label}</FormLabel>
        {conditionalQuestion.type === 'textarea' ? (
          <Textarea
            value={value}
            onChange={(e) =>
              handleInputChange(conditionalQuestion.id, e.target.value)
            }
            placeholder={conditionalQuestion.placeholder}
            rows={conditionalQuestion.rows || 3}
          />
        ) : (
          <Input
            type={conditionalQuestion.type}
            value={value}
            onChange={(e) =>
              handleInputChange(conditionalQuestion.id, e.target.value)
            }
            placeholder={conditionalQuestion.placeholder}
          />
        )}
      </FormControl>
    );
  };

  // Funzione per ottenere l'URL dell'immagine del form
  const getFormImageUrl = (form) => {
    if (form?.image?.url) {
      return form.image.url.startsWith('http')
        ? form.image.url
        : `${process.env.REACT_APP_STRAPI_URL || ''}${form.image.url}`;
    }
    return null;
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Spinner size="xl" color="teal.500" />
            <Text fontSize="lg" color="gray.600">
              Caricamento del form...
            </Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Errore!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!formStructure || !formInfo) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <Alert status="warning" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Form non disponibile</AlertTitle>
              <AlertDescription>
                Il form richiesto non è al momento disponibile.
              </AlertDescription>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  // TEMPORANEO: abilita ENFORCE_AUTH per richiedere autenticazione
  if (ENFORCE_AUTH && (!isAuthenticated || !socio || !socio.id)) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Accesso richiesto</AlertTitle>
              <AlertDescription>
                Devi essere autenticato come socio per compilare questo form.
                <br />
                <Button
                  as={Link}
                  to="/login"
                  colorScheme="teal"
                  size="sm"
                  ml={4}
                >
                  Accedi
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* BREADCRUMB */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.200" py={4}>
        <Container maxW="container.xl">
          <Breadcrumb>
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/forms">
                Form
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <Text color="gray.500">{formInfo.titolo}</Text>
            </BreadcrumbItem>
          </Breadcrumb>
        </Container>
      </Box>

      {/* HERO SECTION */}
      <Box
        position="relative"
        height={{ base: '40vh', md: '50vh' }}
        backgroundImage={
          getFormImageUrl(formInfo)
            ? `url(${getFormImageUrl(formInfo)})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }
        backgroundPosition="center"
        backgroundSize="cover"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.6)"
        />
        <Container
          maxW="container.lg"
          position="relative"
          zIndex="1"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack spacing={4} textAlign="center">
            <HStack spacing={4}>
              <Button
                leftIcon={<FaChevronLeft />}
                variant="ghost"
                color="white"
                onClick={() => navigate('/forms')}
                _hover={{ bg: 'rgba(255,255,255,0.1)' }}
              >
                Torna ai Form
              </Button>
              <Badge
                colorScheme={formInfo.stato === true ? 'teal' : 'red'}
                variant="solid"
                borderRadius="full"
                px={3}
                py={1}
              >
                <HStack spacing={1}>
                  <FaCalendarAlt size={12} />
                  <Text fontSize="xs">
                    {formInfo.stato === true ? 'Attivo' : 'Non Attivo'}
                  </Text>
                </HStack>
              </Badge>
            </HStack>
            <Heading
              as="h1"
              color="white"
              fontSize={{ base: '3xl', md: '5xl' }}
              textShadow="2px 2px 8px rgba(0,0,0,0.6)"
            >
              {formStructure.metadata?.title || formInfo.titolo}
            </Heading>
            <Text
              color="white"
              fontSize={{ base: 'md', md: 'xl' }}
              textShadow="1px 1px 4px rgba(0,0,0,0.6)"
            >
              {formStructure.metadata?.description ||
                formInfo.descrizione ||
                'Compila questo form per condividere la tua opinione'}
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* FORM PRINCIPALE */}
      <Container maxW="container.lg" py={8} px={{ base: 4, md: 8 }}>
        <form onSubmit={handleSubmit}>
          <VStack spacing={8} align="stretch">
            {/* SELEZIONE SOCIO - Mostra solo se ci sono più soci */}
            {isAuthenticated && soci.length > 1 && (
              <Box
                bg="white"
                borderRadius="lg"
                p={6}
                shadow="md"
                border="1px solid"
                borderColor="teal.200"
              >
                <Heading
                  as="h2"
                  size="md"
                  mb={4}
                  color="#3b235a"
                  display="flex"
                  alignItems="center"
                >
                  👤 Seleziona il Socio
                </Heading>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  Scegli quale socio rappresenti per questo form:
                </Text>

                {loadingSoci ? (
                  <HStack spacing={2}>
                    <Spinner size="sm" color="teal.500" />
                    <Text fontSize="sm" color="gray.600">
                      Caricamento soci...
                    </Text>
                  </HStack>
                ) : (
                  <FormControl isRequired>
                    <Select
                      value={selectedSocioId || ''}
                      onChange={(e) => setSelectedSocioId(e.target.value)}
                      placeholder="Seleziona un socio"
                      bg="white"
                      borderColor="teal.300"
                      _hover={{ borderColor: 'teal.400' }}
                      _focus={{
                        borderColor: 'teal.500',
                        boxShadow: '0 0 0 1px teal.500',
                      }}
                    >
                      {soci.map((socioItem) => (
                        <option
                          key={socioItem.id}
                          value={socioItem.documentId || socioItem.id}
                        >
                          {socioItem.nomeSocio} {socioItem.cognomeSocio}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}

            {/* INTRODUZIONE rimossa: ora modificabile solo dal form builder */}

            {/* SEZIONI DINAMICHE */}
            {formStructure.sections &&
              formStructure.sections.map((section) => (
                <Box
                  key={section.id}
                  bg="white"
                  borderRadius="lg"
                  p={8}
                  shadow="md"
                >
                  <Heading
                    as="h2"
                    size="lg"
                    mb={6}
                    color="#3b235a"
                    display="flex"
                    alignItems="center"
                  >
                    {section.title}
                  </Heading>

                  <VStack spacing={6} align="stretch">
                    {section.questions.map((question) => (
                      <FormControl
                        key={question.id}
                        isRequired={question.required}
                      >
                        <FormLabel>{question.label}</FormLabel>
                        {question.description && (
                          <Text fontSize="sm" color="gray.600" mb={4}>
                            {question.description}
                          </Text>
                        )}
                        {renderQuestion(question)}
                        {renderConditionalField(question)}
                      </FormControl>
                    ))}
                  </VStack>
                </Box>
              ))}

            {/* PULSANTE INVIO */}
            <Box textAlign="center" py={8}>
              {/* Messaggio se form già compilato */}
              {hasExistingResponse && (
                <Box
                  mb={6}
                  p={4}
                  bg="orange.50"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="orange.200"
                >
                  <Text
                    fontSize="md"
                    color="orange.700"
                    fontWeight="bold"
                    mb={2}
                  >
                    ⚠️ Form già compilato
                  </Text>
                  <Text fontSize="sm" color="orange.600">
                    Hai già inviato una risposta per questo form. Non puoi
                    compilarlo nuovamente.
                  </Text>
                </Box>
              )}

              {/* Se l'utente non è autenticato, mostra messaggio di login */}
              {!isAuthenticated ? (
                <Box
                  p={6}
                  bg="blue.50"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontSize="lg" color="blue.700" fontWeight="bold" mb={4}>
                    🔐 Per compilare il form devi effettuare il login
                  </Text>
                  <Button
                    as={Link}
                    to="/login"
                    size="lg"
                    colorScheme="blue"
                    bg="#3182ce"
                    _hover={{ bg: '#2c5aa0' }}
                    px={8}
                    py={4}
                    fontSize="lg"
                    fontWeight="bold"
                  >
                    Accedi
                  </Button>
                </Box>
              ) : (
                <>
                  <Button
                    type="submit"
                    size="lg"
                    colorScheme="teal"
                    bg="#3eb297"
                    _hover={{ bg: '#2d8f7a' }}
                    px={12}
                    py={6}
                    fontSize="lg"
                    fontWeight="bold"
                    isLoading={submitting || checkingResponse}
                    loadingText={
                      checkingResponse
                        ? 'Controllo in corso...'
                        : 'Invio in corso...'
                    }
                    isDisabled={
                      formInfo.stato !== true ||
                      (isAuthenticated &&
                        soci.length > 1 &&
                        !selectedSocioId) ||
                      loadingSoci ||
                      hasExistingResponse ||
                      checkingResponse
                    }
                  >
                    {hasExistingResponse
                      ? 'Form già compilato'
                      : formStructure.metadata?.submitButtonText ||
                        'Invia Feedback'}
                  </Button>

                  {/* Messaggio di errore se nessun socio selezionato */}
                  {isAuthenticated &&
                    soci.length > 1 &&
                    !selectedSocioId &&
                    !loadingSoci && (
                      <Text fontSize="sm" color="red.500" mt={2}>
                        Seleziona un socio per continuare
                      </Text>
                    )}
                </>
              )}
            </Box>
          </VStack>
        </form>
      </Container>
    </Box>
  );
};

export default FormDetail;
